import React, { useMemo } from 'react';
import ReactSelect, {
  StylesConfig, GroupBase, Props as ReactSelectProps,
  MultiValue, SingleValue, ActionMeta,
} from 'react-select';
import styled from 'styled-components';

// ─── Types ─────────────────────────────────────────────────────────────────────
export interface SelectOption {
  value: string | number;
  label: string;
  isDisabled?: boolean;
}

// ─── Token Map ─────────────────────────────────────────────────────────────────
const C = {
  gold:        '#BC9979',
  goldLight:   'rgba(188,153,121,0.12)',
  goldFocus:   'rgba(188,153,121,0.20)',
  dark:        '#2D3142',
  border:      '#E0E0E0',
  borderHover: '#C8C8C8',
  error:       '#E53935',
  errorBg:     '#FFF5F5',
  errorFocus:  'rgba(229,57,53,0.12)',
  label:       '#5A5E72',
  placeholder: '#BDBDBD',
  bg:          '#FFFFFF',
  menuBg:      '#FFFFFF',
  optionHover: '#FAF5F0',
  optionSel:   '#F4E8DC',
  optionSelTx: '#2D3142',
  multiTag:    '#F4E8DC',
  multiTagTx:  '#6E4E40',
  hint:        '#9E9E9E',
  text:        '#1A1A1A',
  disabledBg:  '#F7F7F7',
};

// ─── Styled wrappers ────────────────────────────────────────────────────────────
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${C.label};
  letter-spacing: 0.4px;
  text-transform: capitalize;
  user-select: none;
`;

const Required = styled.span`
  color: ${C.error};
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
`;

const ErrorMsg = styled.p`
  margin: 0;
  font-size: 11.5px;
  color: ${C.error};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  &::before { content: '⚠'; font-size: 11px; }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 11px;
  color: ${C.hint};
`;

// ─── React-Select custom styles factory ───────────────────────────────────────
function buildStyles(hasError: boolean): StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      fontSize: '13.5px',
      fontFamily: 'inherit',
      backgroundColor: state.isDisabled ? C.disabledBg : C.bg,
      border: `1.5px solid ${hasError ? C.error : state.isFocused ? C.gold : C.border}`,
      borderRadius: '9px',
      boxShadow: state.isFocused
        ? `0 0 0 3.5px ${hasError ? C.errorFocus : C.goldLight}`
        : 'none',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: hasError ? C.error : state.isFocused ? C.gold : C.borderHover,
      },
    }),

    placeholder: (base) => ({
      ...base,
      color: C.placeholder,
      fontSize: '13px',
    }),

    singleValue: (base) => ({
      ...base,
      color: C.text,
      fontSize: '13.5px',
    }),

    input: (base) => ({
      ...base,
      color: C.text,
      fontSize: '13.5px',
    }),

    indicatorSeparator: () => ({
      display: 'none',
    }),

    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? C.gold : '#9E9E9E',
      transition: 'color 0.18s, transform 0.18s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)',
      padding: '6px 10px',
    }),

    clearIndicator: (base) => ({
      ...base,
      color: '#9E9E9E',
      padding: '6px 4px',
      '&:hover': { color: C.error },
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: C.menuBg,
      border: `1.5px solid ${C.border}`,
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
      overflow: 'hidden',
      zIndex: 9999,
      marginTop: '4px',
    }),

    menuList: (base) => ({
      ...base,
      padding: '4px',
      maxHeight: '220px',
    }),

    option: (base, state) => ({
      ...base,
      fontSize: '13.5px',
      color: state.isSelected ? C.optionSelTx : C.text,
      backgroundColor: state.isSelected
        ? C.optionSel
        : state.isFocused
        ? C.optionHover
        : 'transparent',
      borderRadius: '6px',
      padding: '9px 12px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      fontWeight: state.isSelected ? 600 : 400,
      transition: 'background 0.12s',
      '&:active': { backgroundColor: C.optionSel },
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: C.multiTag,
      borderRadius: '6px',
      padding: '1px 2px',
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: C.multiTagTx,
      fontSize: '12px',
      fontWeight: 600,
      padding: '2px 6px',
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: C.multiTagTx,
      borderRadius: '0 6px 6px 0',
      '&:hover': { backgroundColor: '#E8CCBA', color: C.dark },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: '4px 10px',
      gap: '4px',
    }),

    noOptionsMessage: (base) => ({
      ...base,
      fontSize: '13px',
      color: C.hint,
      padding: '12px',
    }),

    loadingMessage: (base) => ({
      ...base,
      fontSize: '13px',
      color: C.hint,
    }),

    groupHeading: (base) => ({
      ...base,
      fontSize: '10px',
      fontWeight: 700,
      color: '#9E9E9E',
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      padding: '8px 12px 4px',
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 10000,
    }),
  };
}

// ─── FormSelect Props ──────────────────────────────────────────────────────────
export interface FormSelectProps
  extends Omit<ReactSelectProps<SelectOption, boolean, GroupBase<SelectOption>>,
    'options' | 'onChange' | 'value' | 'isMulti'> {
  label?:      string;
  options:     SelectOption[];
  value?:      SelectOption | SelectOption[] | null;
  onChange?:   (value: SelectOption | SelectOption[] | null) => void;
  error?:      string;
  hint?:       string;
  required?:   boolean;
  isMulti?:    boolean;
  sortAlpha?:  boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  hint,
  required  = false,
  isMulti   = false,
  sortAlpha = true,
  placeholder = 'Select...',
  isClearable = true,
  isSearchable = true,
  ...rest
}) => {
  // Rule 4: Dropdown options displayed in alphabet format
  const sortedOptions = useMemo(() => {
    if (!sortAlpha) return options;
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }, [options, sortAlpha]);

  const styles = useMemo(() => buildStyles(!!error), [error]);
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

  const handleChange = (
    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
    _meta: ActionMeta<SelectOption>,
  ) => {
    if (!onChange) return;
    if (isMulti) {
      onChange((newValue as MultiValue<SelectOption>) as SelectOption[]);
    } else {
      onChange((newValue as SingleValue<SelectOption>) as SelectOption | null);
    }
  };

  return (
    <Wrapper>
      {label && (
        <LabelRow>
          <Label>{label}</Label>
          {required && <Required aria-hidden="true">*</Required>}
        </LabelRow>
      )}

      <ReactSelect<SelectOption, boolean, GroupBase<SelectOption>>
        options={sortedOptions}
        value={value ?? null}
        onChange={handleChange}
        styles={styles}
        isMulti={isMulti}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        classNamePrefix="zselect"
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        menuShouldBlockScroll={false}
        noOptionsMessage={() => 'No options found'}
        aria-required={required}
        aria-invalid={!!error}
        {...rest}
      />

      {error ? <ErrorMsg role="alert">{error}</ErrorMsg>
             : hint ? <Hint>{hint}</Hint> : null}
    </Wrapper>
  );
};

export default FormSelect;
